import React, { useState } from 'react';

const marketLocations = [
  // Headquarters
  { id: 'australia', name: 'Australia', subtitle: 'Headquarters', x: 82, y: 75, color: 'teal', size: 'lg', isHQ: true },
  
  // Primary Markets - Asia
  { id: 'india', name: 'India', subtitle: 'South Asia', x: 62, y: 42, color: 'amber', size: 'md', status: 'active' },
  { id: 'china', name: 'China', subtitle: 'East Asia', x: 75, y: 35, color: 'amber', size: 'md', status: 'active' },
  { id: 'japan', name: 'Japan', subtitle: 'East Asia', x: 85, y: 32, color: 'amber', size: 'sm', status: 'active' },
  { id: 'korea', name: 'South Korea', subtitle: 'East Asia', x: 82, y: 35, color: 'amber', size: 'sm', status: 'active' },
  
  // Southeast Asia
  { id: 'singapore', name: 'Singapore', subtitle: 'Southeast Asia', x: 72, y: 55, color: 'emerald', size: 'sm', status: 'active' },
  { id: 'indonesia', name: 'Indonesia', subtitle: 'Southeast Asia', x: 76, y: 62, color: 'emerald', size: 'sm', status: 'active' },
  { id: 'vietnam', name: 'Vietnam', subtitle: 'Southeast Asia', x: 73, y: 45, color: 'emerald', size: 'sm', status: 'active' },
  { id: 'malaysia', name: 'Malaysia', subtitle: 'Southeast Asia', x: 70, y: 52, color: 'emerald', size: 'sm', status: 'active' },
  { id: 'thailand', name: 'Thailand', subtitle: 'Southeast Asia', x: 69, y: 47, color: 'emerald', size: 'sm', status: 'active' },
  
  // Middle East
  { id: 'uae', name: 'UAE', subtitle: 'Middle East', x: 52, y: 42, color: 'amber', size: 'sm', status: 'active' },
  { id: 'saudi', name: 'Saudi Arabia', subtitle: 'Middle East', x: 48, y: 45, color: 'emerald', size: 'sm', status: 'active' },
  
  // Future Markets - Europe
  { id: 'uk', name: 'United Kingdom', subtitle: 'Europe', x: 30, y: 28, color: 'slate', size: 'sm', status: 'future' },
  { id: 'germany', name: 'Germany', subtitle: 'Europe', x: 35, y: 30, color: 'slate', size: 'sm', status: 'future' },
  { id: 'netherlands', name: 'Netherlands', subtitle: 'Europe', x: 32, y: 28, color: 'slate', size: 'sm', status: 'future' },
  
  // Future Markets - Africa
  { id: 'southafrica', name: 'South Africa', subtitle: 'Africa', x: 42, y: 72, color: 'slate', size: 'sm', status: 'future' },
  { id: 'kenya', name: 'Kenya', subtitle: 'East Africa', x: 48, y: 55, color: 'slate', size: 'sm', status: 'future' },
  { id: 'nigeria', name: 'Nigeria', subtitle: 'West Africa', x: 35, y: 50, color: 'slate', size: 'sm', status: 'future' },
  
  // Future Markets - Americas
  { id: 'usa', name: 'United States', subtitle: 'North America', x: 18, y: 35, color: 'slate', size: 'sm', status: 'future' },
  { id: 'brazil', name: 'Brazil', subtitle: 'South America', x: 25, y: 65, color: 'slate', size: 'sm', status: 'future' },
  { id: 'chile', name: 'Chile', subtitle: 'South America', x: 22, y: 75, color: 'slate', size: 'sm', status: 'future' },
];

const tradeRoutes = [
  // Active bidirectional routes
  { from: 'australia', to: 'india', animated: true, status: 'active' },
  { from: 'australia', to: 'china', animated: true, status: 'active' },
  { from: 'australia', to: 'singapore', animated: true, status: 'active' },
  { from: 'australia', to: 'japan', animated: true, status: 'active' },
  { from: 'australia', to: 'uae', animated: true, status: 'active' },
  { from: 'australia', to: 'indonesia', animated: true, status: 'active' },
  { from: 'australia', to: 'vietnam', animated: false, status: 'active' },
  { from: 'australia', to: 'korea', animated: false, status: 'active' },
  { from: 'australia', to: 'thailand', animated: false, status: 'active' },
  { from: 'australia', to: 'malaysia', animated: false, status: 'active' },
  { from: 'australia', to: 'saudi', animated: false, status: 'active' },
  
  // Future routes
  { from: 'australia', to: 'uk', animated: false, status: 'future' },
  { from: 'australia', to: 'germany', animated: false, status: 'future' },
  { from: 'australia', to: 'southafrica', animated: false, status: 'future' },
  { from: 'australia', to: 'usa', animated: false, status: 'future' },
  { from: 'australia', to: 'brazil', animated: false, status: 'future' },
];

export const InteractiveMap = () => {
  const [activeMarket, setActiveMarket] = useState(null);

  const getMarketById = (id) => marketLocations.find(m => m.id === id);

  const getSizeClasses = (size, isActive) => {
    const baseSize = {
      lg: 'w-5 h-5',
      md: 'w-4 h-4',
      sm: 'w-3 h-3',
    };
    return `${baseSize[size]} ${isActive ? 'scale-150' : ''}`;
  };

  const getColorClasses = (color, status) => {
    if (status === 'future') {
      return 'bg-slate-400/60 shadow-slate-400/30 border border-dashed border-slate-300';
    }
    const colors = {
      teal: 'bg-teal-500 shadow-teal-500/50',
      amber: 'bg-amber-500 shadow-amber-500/50',
      emerald: 'bg-emerald-500 shadow-emerald-500/50',
      slate: 'bg-slate-400 shadow-slate-400/30',
    };
    return colors[color];
  };

  return (
    <div className="relative w-full h-[550px] bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 rounded-3xl overflow-hidden">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      {/* Decorative Circles */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="w-[700px] h-[700px] rounded-full border border-white/5 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        <div className="w-[500px] h-[500px] rounded-full border border-white/5 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        <div className="w-[300px] h-[300px] rounded-full border border-white/10 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      </div>

      {/* SVG for Trade Routes */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          {/* Glow filter */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          {/* Export arrow marker */}
          <marker id="arrowExport" markerWidth="4" markerHeight="4" refX="2" refY="2" orient="auto">
            <path d="M0,0 L4,2 L0,4 Z" fill="#14b8a6" />
          </marker>
          
          {/* Import arrow marker */}
          <marker id="arrowImport" markerWidth="4" markerHeight="4" refX="2" refY="2" orient="auto">
            <path d="M0,0 L4,2 L0,4 Z" fill="#f59e0b" />
          </marker>
        </defs>

        {/* Trade Routes */}
        {tradeRoutes.map((route, index) => {
          const from = getMarketById(route.from);
          const to = getMarketById(route.to);
          if (!from || !to) return null;

          const isActive = activeMarket === route.from || activeMarket === route.to;
          const isFuture = route.status === 'future';
          
          // Calculate curved path
          const midX = (from.x + to.x) / 2;
          const midY = (from.y + to.y) / 2 - 8;
          const pathD = `M ${from.x} ${from.y} Q ${midX} ${midY} ${to.x} ${to.y}`;
          
          // Reverse path for import animation
          const reversePathD = `M ${to.x} ${to.y} Q ${midX} ${midY} ${from.x} ${from.y}`;

          return (
            <g key={index}>
              {/* Base route line */}
              <path
                d={pathD}
                fill="none"
                stroke={isFuture ? "#475569" : (isActive ? "#14b8a6" : "#334e68")}
                strokeWidth={isActive ? "0.3" : "0.15"}
                strokeDasharray={isFuture ? "1 1" : "none"}
                className="transition-all duration-300"
                style={{
                  filter: isActive && !isFuture ? 'url(#glow)' : 'none',
                  opacity: isFuture ? 0.4 : 1,
                }}
              />
              
              {/* Animated dots for active routes - Export (from Australia) */}
              {route.animated && !isFuture && (
                <>
                  {/* Export dot - Teal going OUT */}
                  <circle r="0.6" fill="#14b8a6" filter="url(#glow)">
                    <animateMotion
                      dur={`${4 + (index * 0.5)}s`}
                      repeatCount="indefinite"
                      path={pathD}
                    />
                  </circle>
                  
                  {/* Import dot - Amber coming IN */}
                  <circle r="0.6" fill="#f59e0b" filter="url(#glow)">
                    <animateMotion
                      dur={`${4.5 + (index * 0.5)}s`}
                      repeatCount="indefinite"
                      path={reversePathD}
                    />
                  </circle>
                </>
              )}
            </g>
          );
        })}
      </svg>

      {/* Market Points */}
      {marketLocations.map((market) => {
        const isActive = activeMarket === market.id;
        const isFuture = market.status === 'future';
        return (
          <div
            key={market.id}
            className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group"
            style={{ left: `${market.x}%`, top: `${market.y}%` }}
            onMouseEnter={() => setActiveMarket(market.id)}
            onMouseLeave={() => setActiveMarket(null)}
          >
            {/* Pulse animation for HQ */}
            {market.isHQ && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 rounded-full bg-teal-500/30 animate-ping" />
              </div>
            )}
            
            {/* Market dot */}
            <div
              className={`
                relative rounded-full shadow-lg transition-all duration-300 z-10
                ${getSizeClasses(market.size, isActive)}
                ${getColorClasses(market.color, market.status)}
                ${isActive ? 'ring-4 ring-white/30' : ''}
                ${isFuture ? 'opacity-60' : ''}
              `}
            />

            {/* Tooltip */}
            <div
              className={`
                absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-3 py-2 
                bg-white rounded-lg shadow-xl whitespace-nowrap
                transition-all duration-200 z-20
                ${isActive ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'}
              `}
            >
              <div className="text-sm font-semibold text-navy-900">{market.name}</div>
              <div className="text-xs text-slate-500">{market.subtitle}</div>
              {market.isHQ && (
                <div className="text-xs text-teal-600 font-medium mt-1">Naviton Global HQ</div>
              )}
              {isFuture && (
                <div className="text-xs text-slate-400 font-medium mt-1">Future Market</div>
              )}
              {/* Arrow */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-white" />
            </div>
          </div>
        );
      })}

      {/* Legend */}
      <div className="absolute bottom-6 left-6 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
          <div className="w-3 h-3 rounded-full bg-teal-500 shadow-lg shadow-teal-500/50" />
          <span className="text-xs text-white/90">Australia (HQ)</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
          <div className="w-3 h-3 rounded-full bg-amber-500 shadow-lg shadow-amber-500/50" />
          <span className="text-xs text-white/90">Primary Markets</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
          <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/50" />
          <span className="text-xs text-white/90">Growing Markets</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
          <div className="w-3 h-3 rounded-full bg-slate-400/60 border border-dashed border-slate-300" />
          <span className="text-xs text-white/90">Future Markets</span>
        </div>
      </div>

      {/* Trade Flow Indicator */}
      <div className="absolute bottom-6 right-6 flex flex-col gap-2">
        <div className="flex items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-teal-500" />
            <span className="text-xs text-white/70">→</span>
          </div>
          <span className="text-xs text-white/90">Export</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-xs text-white/70">→</span>
          </div>
          <span className="text-xs text-white/90">Import</span>
        </div>
      </div>

      {/* Stats Overlay */}
      <div className="absolute top-6 right-6 flex gap-3">
        <div className="px-4 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 text-center">
          <div className="text-2xl font-bold text-white">50+</div>
          <div className="text-xs text-white/70">Countries</div>
        </div>
        <div className="px-4 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 text-center">
          <div className="text-2xl font-bold text-teal-400">6</div>
          <div className="text-xs text-white/70">Active Regions</div>
        </div>
        <div className="px-4 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 text-center">
          <div className="text-2xl font-bold text-slate-400">3</div>
          <div className="text-xs text-white/70">Expansion Regions</div>
        </div>
      </div>

      {/* Title */}
      <div className="absolute top-6 left-6">
        <h3 className="text-xl font-bold text-white mb-1">Global Trade Network</h3>
        <p className="text-sm text-white/60">Bidirectional import & export routes</p>
      </div>
    </div>
  );
};
